import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { listDevicePolicies, listSamsungPolicies, listSonosPolicies } from '../services/aiTokensApi';
import { listAndroidTvPolicies } from '../installed/androidtv';
import { listLgPolicies } from '../installed/air';
import { listEvohomePolicies } from '../installed/heat';
import { listPiletaPolicies } from '../installed/pileta';
import { eventBus } from '../services/EventBus';
import { getUser } from '../services/auth';

export type AiPolicyLookupEntry = { whitelisted: boolean; blacklisted: boolean };

export type AiPolicyLookup = Record<number, AiPolicyLookupEntry>;

/** Sin fila de política, o fila sin lista blanca ni lista negra. */
export function isAiPolicyUnset(entry: AiPolicyLookupEntry | undefined): boolean {
  if (!entry) return true;
  if (entry.blacklisted) return false;
  return !entry.whitelisted;
}

export async function fetchAiDevicePolicyLookup(): Promise<AiPolicyLookup> {
  const acc: AiPolicyLookup = {};
  let offset = 0;
  const page = 500;
  for (;;) {
    const rows = await listDevicePolicies({ limit: page, offset });
    rows.forEach((r) => {
      acc[r.device_id] = {
        whitelisted: Boolean(r.whitelisted),
        blacklisted: Boolean(r.blacklisted),
      };
    });
    if (rows.length < page) break;
    offset += page;
  }
  return acc;
}

// Lookup paralelo para parlantes Sonos (indexado por sonos_speaker_id).
// El backend solo devuelve parlantes CON política definida.
export async function fetchAiSonosPolicyLookup(): Promise<AiPolicyLookup> {
  const acc: AiPolicyLookup = {};
  const rows = await listSonosPolicies();
  rows.forEach((r) => {
    acc[r.sonos_speaker_id] = {
      whitelisted: Boolean(r.whitelist),
      blacklisted: Boolean(r.blacklist),
    };
  });
  return acc;
}

// Lookup paralelo para dispositivos Samsung (indexado por samsung_device_id).
export async function fetchAiSamsungPolicyLookup(): Promise<AiPolicyLookup> {
  const acc: AiPolicyLookup = {};
  const rows = await listSamsungPolicies();
  rows.forEach((r) => {
    acc[r.samsung_device_id] = {
      whitelisted: Boolean(r.whitelist),
      blacklisted: Boolean(r.blacklist),
    };
  });
  return acc;
}

// Lookup paralelo para Android TV (indexado por androidtv_device_id).
export async function fetchAiAndroidTvPolicyLookup(): Promise<AiPolicyLookup> {
  const acc: AiPolicyLookup = {};
  const rows = await listAndroidTvPolicies();
  rows.forEach((r) => {
    acc[r.androidtv_device_id] = {
      whitelisted: Boolean(r.whitelist),
      blacklisted: Boolean(r.blacklist),
    };
  });
  return acc;
}

export async function fetchAiLgPolicyLookup(): Promise<AiPolicyLookup> {
  const acc: AiPolicyLookup = {};
  const rows = await listLgPolicies();
  rows.forEach((r) => {
    acc[r.lg_device_id] = {
      whitelisted: Boolean(r.whitelist),
      blacklisted: Boolean(r.blacklist),
    };
  });
  return acc;
}

export async function fetchAiEvohomePolicyLookup(): Promise<AiPolicyLookup> {
  const acc: AiPolicyLookup = {};
  const rows = await listEvohomePolicies();
  rows.forEach((r) => {
    acc[r.evohome_zone_id] = {
      whitelisted: Boolean(r.whitelist),
      blacklisted: Boolean(r.blacklist),
    };
  });
  return acc;
}

export async function fetchAiPiletaPolicyLookup(): Promise<AiPolicyLookup> {
  const acc: AiPolicyLookup = {};
  const rows = await listPiletaPolicies();
  rows.forEach((r) => {
    acc[r.pileta_device_id] = {
      whitelisted: Boolean(r.whitelist),
      blacklisted: Boolean(r.blacklist),
    };
  });
  return acc;
}

export interface AiDevicePolicyContextValue {
  lookup: AiPolicyLookup;
  /** Lookup de parlantes Sonos (indexado por sonos_speaker_id). */
  sonosLookup: AiPolicyLookup;
  /** Lookup de dispositivos Samsung (indexado por samsung_device_id). */
  samsungLookup: AiPolicyLookup;
  androidtvLookup: AiPolicyLookup;
  lgLookup: AiPolicyLookup;
  evohomeLookup: AiPolicyLookup;
  piletaLookup: AiPolicyLookup;
  /** true cuando terminó el último intento de carga (admin o no). */
  ready: boolean;
  /** Incrementa en cada carga exitosa o vaciado; útil para dependencias de useMemo. */
  revision: number;
  isWhitelistedForIcon(deviceId: number): boolean;
  /** Igual que isWhitelistedForIcon pero para parlantes Sonos. */
  isSonosWhitelistedForIcon(speakerId: number): boolean;
  /** Igual que isWhitelistedForIcon pero para dispositivos Samsung. */
  isSamsungWhitelistedForIcon(deviceId: number): boolean;
  isAndroidTvWhitelistedForIcon(deviceId: number): boolean;
  isLgWhitelistedForIcon(deviceId: number): boolean;
  isEvohomeWhitelistedForIcon(deviceId: number): boolean;
  isPiletaWhitelistedForIcon(deviceId: number): boolean;
  /** Lista negra explícita: el modelo no puede controlar el dispositivo. */
  isBlacklistedForIcon(deviceId: number): boolean;
  isSonosBlacklistedForIcon(speakerId: number): boolean;
  isSamsungBlacklistedForIcon(deviceId: number): boolean;
  isAndroidTvBlacklistedForIcon(deviceId: number): boolean;
  isLgBlacklistedForIcon(deviceId: number): boolean;
  isEvohomeBlacklistedForIcon(deviceId: number): boolean;
  isPiletaBlacklistedForIcon(deviceId: number): boolean;
}

const defaultValue: AiDevicePolicyContextValue = {
  lookup: {},
  sonosLookup: {},
  samsungLookup: {},
  androidtvLookup: {},
  lgLookup: {},
  evohomeLookup: {},
  piletaLookup: {},
  ready: true,
  revision: 0,
  isWhitelistedForIcon: () => false,
  isSonosWhitelistedForIcon: () => false,
  isSamsungWhitelistedForIcon: () => false,
  isAndroidTvWhitelistedForIcon: () => false,
  isLgWhitelistedForIcon: () => false,
  isEvohomeWhitelistedForIcon: () => false,
  isPiletaWhitelistedForIcon: () => false,
  isBlacklistedForIcon: () => false,
  isSonosBlacklistedForIcon: () => false,
  isSamsungBlacklistedForIcon: () => false,
  isAndroidTvBlacklistedForIcon: () => false,
  isLgBlacklistedForIcon: () => false,
  isEvohomeBlacklistedForIcon: () => false,
  isPiletaBlacklistedForIcon: () => false,
};

const AiDevicePolicyContext = createContext<AiDevicePolicyContextValue>(defaultValue);

/**
 * Superficie donde sí se pintan badges «IA» en tarjetas (solo grilla Settings → Dispositivos).
 * Fuera de este provider, incluso un admin no ve la etiqueta en las tarjetas.
 */
const AiCardBadgeSurfaceContext = createContext(false);

export function AiCardBadgeSurfaceProvider({ children }: { children: React.ReactNode }) {
  return (
    <AiCardBadgeSurfaceContext.Provider value={true}>
      {children}
    </AiCardBadgeSurfaceContext.Provider>
  );
}

/** true solo dentro de la grilla de dispositivos de Settings. */
export function useShowAiCardBadge(): boolean {
  return useContext(AiCardBadgeSurfaceContext);
}

export function AiDevicePolicyProvider({ children }: { children: React.ReactNode }) {
  const [lookup, setLookup] = useState<AiPolicyLookup>({});
  const [sonosLookup, setSonosLookup] = useState<AiPolicyLookup>({});
  const [samsungLookup, setSamsungLookup] = useState<AiPolicyLookup>({});
  const [androidtvLookup, setAndroidtvLookup] = useState<AiPolicyLookup>({});
  const [lgLookup, setLgLookup] = useState<AiPolicyLookup>({});
  const [evohomeLookup, setEvohomeLookup] = useState<AiPolicyLookup>({});
  const [piletaLookup, setPiletaLookup] = useState<AiPolicyLookup>({});
  const [ready, setReady] = useState(false);
  const [revision, setRevision] = useState(0);

  const reload = useCallback(async () => {
    const u = getUser();
    if (!u || u.role !== 'admin') {
      setLookup({});
      setSonosLookup({});
      setSamsungLookup({});
      setAndroidtvLookup({});
      setLgLookup({});
      setEvohomeLookup({});
      setPiletaLookup({});
      setReady(true);
      setRevision((r) => r + 1);
      return;
    }
    try {
      const acc = await fetchAiDevicePolicyLookup();
      setLookup(acc);
    } catch {
      setLookup({});
    }
    try {
      const accSonos = await fetchAiSonosPolicyLookup();
      setSonosLookup(accSonos);
    } catch {
      setSonosLookup({});
    }
    try {
      const accSamsung = await fetchAiSamsungPolicyLookup();
      setSamsungLookup(accSamsung);
    } catch {
      setSamsungLookup({});
    }
    try {
      const accAndroidTv = await fetchAiAndroidTvPolicyLookup();
      setAndroidtvLookup(accAndroidTv);
    } catch {
      setAndroidtvLookup({});
    }
    try {
      const accLg = await fetchAiLgPolicyLookup();
      setLgLookup(accLg);
    } catch {
      setLgLookup({});
    }
    try {
      const accEvohome = await fetchAiEvohomePolicyLookup();
      setEvohomeLookup(accEvohome);
    } catch {
      setEvohomeLookup({});
    }
    try {
      const accPileta = await fetchAiPiletaPolicyLookup();
      setPiletaLookup(accPileta);
    } catch {
      setPiletaLookup({});
    } finally {
      setReady(true);
      setRevision((r) => r + 1);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const u1 = eventBus.on('ai:device-policy-changed', () => {
      void reload();
    });
    const u2 = eventBus.on('ai:policy-lookup-refresh', () => {
      void reload();
    });
    const u3 = eventBus.on('ai:sonos-policy-changed', () => {
      void reload();
    });
    const u4 = eventBus.on('ai:samsung-policy-changed', () => {
      void reload();
    });
    const u5 = eventBus.on('ai:androidtv-policy-changed', () => {
      void reload();
    });
    const u6 = eventBus.on('ai:lg-policy-changed', () => {
      void reload();
    });
    const u7 = eventBus.on('ai:evohome-policy-changed', () => {
      void reload();
    });
    const u8 = eventBus.on('ai:pileta-policy-changed', () => {
      void reload();
    });
    return () => {
      u1();
      u2();
      u3();
      u4();
      u5();
      u6();
      u7();
      u8();
    };
  }, [reload]);

  const value = useMemo<AiDevicePolicyContextValue>(() => {
    const u = getUser();
    const isAdminReady = Boolean(u && u.role === 'admin' && ready);
    const isWhitelistedForIcon = (deviceId: number) => {
      if (!isAdminReady) return false;
      const p = lookup[deviceId];
      return Boolean(p && p.whitelisted && !p.blacklisted);
    };
    const isSonosWhitelistedForIcon = (speakerId: number) => {
      if (!isAdminReady) return false;
      const p = sonosLookup[speakerId];
      return Boolean(p && p.whitelisted && !p.blacklisted);
    };
    const isSamsungWhitelistedForIcon = (deviceId: number) => {
      if (!isAdminReady) return false;
      const p = samsungLookup[deviceId];
      return Boolean(p && p.whitelisted && !p.blacklisted);
    };
    const isAndroidTvWhitelistedForIcon = (deviceId: number) => {
      if (!isAdminReady) return false;
      const p = androidtvLookup[deviceId];
      return Boolean(p && p.whitelisted && !p.blacklisted);
    };
    const isLgWhitelistedForIcon = (deviceId: number) => {
      if (!isAdminReady) return false;
      const p = lgLookup[deviceId];
      return Boolean(p && p.whitelisted && !p.blacklisted);
    };
    const isEvohomeWhitelistedForIcon = (deviceId: number) => {
      if (!isAdminReady) return false;
      const p = evohomeLookup[deviceId];
      return Boolean(p && p.whitelisted && !p.blacklisted);
    };
    const isPiletaWhitelistedForIcon = (deviceId: number) => {
      if (!isAdminReady) return false;
      const p = piletaLookup[deviceId];
      return Boolean(p && p.whitelisted && !p.blacklisted);
    };
    const isDenied = (p: AiPolicyLookupEntry | undefined) => Boolean(p && p.blacklisted);
    const isBlacklistedForIcon = (deviceId: number) => isAdminReady && isDenied(lookup[deviceId]);
    const isSonosBlacklistedForIcon = (speakerId: number) => isAdminReady && isDenied(sonosLookup[speakerId]);
    const isSamsungBlacklistedForIcon = (deviceId: number) => isAdminReady && isDenied(samsungLookup[deviceId]);
    const isAndroidTvBlacklistedForIcon = (deviceId: number) => isAdminReady && isDenied(androidtvLookup[deviceId]);
    const isLgBlacklistedForIcon = (deviceId: number) => isAdminReady && isDenied(lgLookup[deviceId]);
    const isEvohomeBlacklistedForIcon = (deviceId: number) => isAdminReady && isDenied(evohomeLookup[deviceId]);
    const isPiletaBlacklistedForIcon = (deviceId: number) => isAdminReady && isDenied(piletaLookup[deviceId]);
    return {
      lookup,
      sonosLookup,
      samsungLookup,
      androidtvLookup,
      lgLookup,
      evohomeLookup,
      piletaLookup,
      ready,
      revision,
      isWhitelistedForIcon,
      isSonosWhitelistedForIcon,
      isSamsungWhitelistedForIcon,
      isAndroidTvWhitelistedForIcon,
      isLgWhitelistedForIcon,
      isEvohomeWhitelistedForIcon,
      isPiletaWhitelistedForIcon,
      isBlacklistedForIcon,
      isSonosBlacklistedForIcon,
      isSamsungBlacklistedForIcon,
      isAndroidTvBlacklistedForIcon,
      isLgBlacklistedForIcon,
      isEvohomeBlacklistedForIcon,
      isPiletaBlacklistedForIcon,
    };
  }, [lookup, sonosLookup, samsungLookup, androidtvLookup, lgLookup, evohomeLookup, piletaLookup, ready, revision]);

  return (
    <AiDevicePolicyContext.Provider value={value}>{children}</AiDevicePolicyContext.Provider>
  );
}

export function useAiDevicePolicy(): AiDevicePolicyContextValue {
  return useContext(AiDevicePolicyContext);
}
